import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { SEOWebPage, SEOOrganization, SEOFAQ } from '@/components/base/SEOSchema';
import { reviews, faqs, siteInfo } from '@/mocks/comics';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9pe1c1n2trhb35gvdlg';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    try {
      const formData = new FormData(form);
      formData.delete('website_alt');
      const res = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || text;
      const isSpam = typeof serverMsg === 'string' && serverMsg.includes('spam');
      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOWebPage
        title="关于我们-51漫画网-洞洞杂货铺韩漫全集免费阅读平台"
        description={`关于51漫画网：${siteInfo.description} 成立于${siteInfo.founded}，已收录${siteInfo.stats.totalComics}+部漫画，服务${(siteInfo.stats.totalUsers / 10000).toFixed(0)}万+用户。`}
        url="https://ytzshn.cn/about"
        breadcrumbItems={[
          { name: '首页', url: 'https://ytzshn.cn' },
          { name: '关于我们', url: 'https://ytzshn.cn/about' },
        ]}
      />
      <SEOOrganization />
      <SEOFAQ faqs={faqs} />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]} />

          {/* Page Title */}
          <div className="py-8 text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">关于51漫画网</h1>
            <p className="text-sm text-foreground-500 mt-3 max-w-lg mx-auto">
              洞洞杂货铺韩漫全集免费阅读平台，用心服务每一位韩漫爱好者
            </p>
          </div>

          {/* Brand Introduction */}
          <section className="pb-12" aria-label="品牌介绍">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-5 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-primary-500 inline-block"></span>
              品牌介绍
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  <strong>51漫画网</strong>成立于{siteInfo.founded}，是国内领先的韩漫全集免费阅读平台。我们专注于为韩漫爱好者提供最新、最全、最高清的漫画阅读体验。平台收录了包括<strong>《洞洞杂货铺》</strong>在内的{siteInfo.stats.totalComics}余部优质韩漫作品，涵盖恋爱、奇幻、热血、悬疑、校园、职场等多种类型。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们深知每一位漫画爱好者的需求——不仅要看得过瘾，还要看得方便。因此，51漫画网采用先进的响应式设计，完美适配手机、平板和电脑屏幕，让您随时随地享受流畅的韩漫阅读体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们的团队由一群热爱漫画的年轻人组成，坚持<strong>专注韩漫、每日更新、全部免费</strong>的理念，努力打造最受韩漫爱好者喜爱的在线阅读社区。
                </p>
              </div>
              <div className="rounded-xl overflow-hidden h-[280px]">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20modern%20creative%20office%20space%20with%20comic%20art%20on%20walls%2C%20people%20working%20at%20computers%20with%20webtoon%20displays%2C%20bookshelves%20filled%20with%20comics%20and%20manga%2C%20warm%20lighting%2C%20creative%20and%20vibrant%20atmosphere%2C%20Korean%20style%20office%20interior&width=600&height=400&seq=about-brand&orientation=landscape"
                  alt="51漫画网团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
              {[
                { value: `${siteInfo.stats.totalComics}+`, label: '收录漫画' },
                { value: `${(siteInfo.stats.totalChapters / 10000).toFixed(0)}万+`, label: '漫画章节' },
                { value: `${(siteInfo.stats.totalUsers / 10000).toFixed(0)}万+`, label: '注册用户' },
                { value: `${siteInfo.stats.dailyUpdate}+`, label: '每日更新' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-5 rounded-xl bg-background-100">
                  <p className="text-2xl font-bold text-primary-600 font-heading">{stat.value}</p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* User Reviews */}
          <section className="py-12 border-t border-background-200/70" aria-label="用户口碑">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-5 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500 inline-block"></span>
              用户口碑
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {reviews.map((review) => (
                <div key={review.id} className="p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-600">
                        {review.username.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{review.username}</p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i key={i} className={`ri-star-fill text-xs ${i < review.rating ? 'text-amber-400' : 'text-foreground-200'}`}></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                  <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="py-12 border-t border-background-200/70" aria-label="常见问题">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-5 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-secondary-500 inline-block"></span>
              常见问题
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <details key={i} className="group rounded-xl bg-background-100 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="py-12 border-t border-background-200/70" aria-label="联系我们">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-5 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-primary-500 inline-block"></span>
              联系我们
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  如果您在使用51漫画网的过程中遇到任何问题，或有任何建议和意见，欢迎通过以下方式与我们联系。我们的客服团队将在24小时内回复您的消息。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100">
                      <i className="ri-qq-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">QQ群</p>
                      <p className="text-sm font-medium text-foreground-900">{siteInfo.qq}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100">
                      <i className="ri-wechat-line text-lg text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微信公众号</p>
                      <p className="text-sm font-medium text-foreground-900">{siteInfo.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200">
                      <i className="ri-map-pin-line text-lg text-foreground-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">公司地址</p>
                      <p className="text-sm font-medium text-foreground-900">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6">
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-4">发送消息</h3>
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">您的名字</label>
                    <input type="text" id="name" name="name" required className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors" placeholder="请输入您的名字" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">电子邮箱</label>
                    <input type="email" id="email" name="email" required className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors" placeholder="请输入您的邮箱" />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input type="text" id="subject" name="subject" required className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors" placeholder="请输入消息主题" />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                    <textarea id="message" name="message" required maxLength={500} rows={4} className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none" placeholder="请输入您想说的话（最多500字）"></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                  </div>
                  <button type="submit" className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
                    发送消息
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">消息发送成功！我们会尽快回复您。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}